/**
 * Item Context Menu Component
 *
 * Reusable context menu for workspace items (tables, linked tables, folders).
 * Provides standardized menu structure with keyboard shortcuts and proper disabled states.
 */

import {
  FolderOpen,
  ExternalLink,
  Pencil,
  Copy,
  Star,
  FolderInput,
  Trash2,
  RotateCcw,
  AlertCircle,
} from 'lucide-react';
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
} from '@loader/ui';
import type { WorkspaceItemType } from '@loader/shared-types';

import { deleteKey } from '@/lib/platform';

// ============================================================================
// TYPES
// ============================================================================

export interface ItemContextMenuProps {
  id: string;
  type: WorkspaceItemType;
  name?: string;
  isFavorite: boolean;
  isTrash?: boolean;
  /** Number of selected items (for multi-select behavior) */
  selectedCount?: number;
  /** Whether user can edit (editor/owner) - viewers cannot modify items */
  canEdit?: boolean;
  onOpen?: (id: string, type: WorkspaceItemType) => void;
  onOpenInNewTab?: (id: string, type: WorkspaceItemType) => void;
  onRename?: (id: string, type: WorkspaceItemType) => void;
  onDuplicate?: (id: string) => void;
  onToggleFavorite?: (id: string, type: WorkspaceItemType) => void;
  onMove?: (id: string, type: WorkspaceItemType) => void;
  onDelete?: (id: string, type: WorkspaceItemType, name?: string) => void;
  onRestore?: (id: string, type: WorkspaceItemType) => void;
  onDeletePermanently?: (id: string, type: WorkspaceItemType) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Context menu for workspace items with standardized structure
 */
export function ItemContextMenu({
  id,
  type,
  name,
  isFavorite,
  isTrash = false,
  selectedCount = 1,
  canEdit = true,
  onOpen,
  onOpenInNewTab,
  onRename,
  onDuplicate,
  onToggleFavorite,
  onMove,
  onDelete,
  onRestore,
  onDeletePermanently,
}: ItemContextMenuProps) {
  // Multi-select mode: disable single-item actions
  const isMultiSelect = selectedCount > 1;

  // If in trash, show trash-specific menu
  if (isTrash) {
    return (
      <ContextMenuContent>
        <span className={!canEdit ? 'cursor-not-allowed' : ''}>
          <ContextMenuItem
            disabled={!canEdit}
            onClick={canEdit ? () => onRestore?.(id, type) : undefined}
          >
            <RotateCcw className="mr-2 size-4" />
            Restore
          </ContextMenuItem>
        </span>
        <ContextMenuSeparator />
        <span className={!canEdit ? 'cursor-not-allowed' : ''}>
          <ContextMenuItem
            className="text-destructive focus:text-destructive"
            disabled={!canEdit}
            onClick={canEdit ? () => onDeletePermanently?.(id, type) : undefined}
          >
            <AlertCircle className="mr-2 size-4" />
            Delete Permanently
          </ContextMenuItem>
        </span>
      </ContextMenuContent>
    );
  }

  return (
    <ContextMenuContent>
      {/* Open actions - disabled in multi-select */}
      <ContextMenuItem
        disabled={isMultiSelect}
        onClick={() => !isMultiSelect && onOpen?.(id, type)}
      >
        <FolderOpen className="mr-2 size-4" />
        Open
      </ContextMenuItem>
      {onOpenInNewTab && (
        <ContextMenuItem
          disabled={isMultiSelect}
          onClick={() => !isMultiSelect && onOpenInNewTab(id, type)}
        >
          <ExternalLink className="mr-2 size-4" />
          Open in New Tab
        </ContextMenuItem>
      )}

      <ContextMenuSeparator />

      {/* Edit actions - disabled in multi-select or for viewers */}
      <span className={isMultiSelect || !canEdit ? 'cursor-not-allowed' : ''}>
        <ContextMenuItem
          disabled={isMultiSelect || !canEdit}
          onClick={() => !isMultiSelect && canEdit && onRename?.(id, type)}
        >
          <Pencil className="mr-2 size-4" />
          Rename
          <ContextMenuShortcut>F2</ContextMenuShortcut>
        </ContextMenuItem>
      </span>

      {/* Duplicate - only for linked tables, disabled in multi-select or for viewers */}
      {type === 'linked_table' && onDuplicate && (
        <span className={isMultiSelect || !canEdit ? 'cursor-not-allowed' : ''}>
          <ContextMenuItem
            disabled={isMultiSelect || !canEdit}
            onClick={() => !isMultiSelect && canEdit && onDuplicate(id)}
          >
            <Copy className="mr-2 size-4" />
            Duplicate
          </ContextMenuItem>
        </span>
      )}

      {/* Favorite - not available for folders, disabled in multi-select or for viewers */}
      {type !== 'folder' && (
        <span className={isMultiSelect || !canEdit ? 'cursor-not-allowed' : ''}>
          <ContextMenuItem
            disabled={isMultiSelect || !canEdit}
            onClick={() => !isMultiSelect && canEdit && onToggleFavorite?.(id, type)}
          >
            <Star className="mr-2 size-4" />
            {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
          </ContextMenuItem>
        </span>
      )}

      <ContextMenuSeparator />

      {/* Organization actions - enabled in multi-select, disabled for viewers */}
      <span className={!canEdit ? 'cursor-not-allowed' : ''}>
        <ContextMenuItem
          disabled={!canEdit}
          onClick={canEdit ? () => onMove?.(id, type) : undefined}
        >
          <FolderInput className="mr-2 size-4" />
          {isMultiSelect ? `Move ${selectedCount} items` : 'Move to Folder'}
        </ContextMenuItem>
      </span>

      <ContextMenuSeparator />

      {/* Delete action - enabled in multi-select, disabled for viewers */}
      <span className={!canEdit ? 'cursor-not-allowed' : ''}>
        <ContextMenuItem
          className="text-destructive focus:text-destructive"
          disabled={!canEdit}
          onClick={canEdit ? () => onDelete?.(id, type, name) : undefined}
        >
          <Trash2 className="mr-2 size-4" />
          {isMultiSelect ? `Move ${selectedCount} items to Trash` : 'Move to Trash'}
          <ContextMenuShortcut>{deleteKey}</ContextMenuShortcut>
        </ContextMenuItem>
      </span>
    </ContextMenuContent>
  );
}
